import { ClipboardCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PackagingFieldCrossCheckStoredEntry } from '@/shared/hooks/api/packagingSuppliers'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { PackagingFieldCrossCheckHistoryTable } from './PackagingFieldCrossCheckHistoryTable'

type PackagingItemAuditsTabProps = {
  history: PackagingFieldCrossCheckStoredEntry[]
  locale: string
  showRunAudit?: boolean
  onRunAuditClick?: () => void
  showManualOverride?: boolean
  onManualOverrideClick?: () => void
}

function AuditsTabActions({
  showRunAudit,
  onRunAuditClick,
  showManualOverride,
  onManualOverrideClick,
}: {
  showRunAudit: boolean
  onRunAuditClick?: () => void
  showManualOverride: boolean
  onManualOverrideClick?: () => void
}) {
  const { t } = useTranslation('packagingSuppliers')
  const showRun = showRunAudit && onRunAuditClick
  const showOverride = showManualOverride && onManualOverrideClick

  if (!showRun && !showOverride) {
    return null
  }

  return (
    <div className='flex flex-wrap items-center justify-end gap-3'>
      {showOverride ? (
        <Button type='button' variant='outline' onClick={onManualOverrideClick}>
          {t('itemDetail.manualOverride.button')}
        </Button>
      ) : null}
      {showRun ? (
        <Button type='button' variant='outline' onClick={onRunAuditClick}>
          {t('itemDetail.audits.runAudit')}
        </Button>
      ) : null}
    </div>
  )
}

export function PackagingItemAuditsTab({
  history,
  locale,
  showRunAudit = false,
  onRunAuditClick,
  showManualOverride = false,
  onManualOverrideClick,
}: PackagingItemAuditsTabProps) {
  const { t } = useTranslation('packagingSuppliers')
  const showHint = showRunAudit && onRunAuditClick
  const showRun = showRunAudit && onRunAuditClick
  const showOverride = showManualOverride && onManualOverrideClick

  if (history.length === 0) {
    return (
      <div className='space-y-4'>
        {showHint ? (
          <Alert variant='info'>
            <AlertDescription>
              {t('itemDetail.audits.runAuditHint')}
            </AlertDescription>
          </Alert>
        ) : null}
        <Empty className='rounded-lg border border-dashed py-12'>
          <EmptyHeader>
            <EmptyMedia variant='icon'>
              <ClipboardCheck className='size-10' />
            </EmptyMedia>
            <EmptyTitle>{t('itemDetail.audits.empty')}</EmptyTitle>
          </EmptyHeader>
          {showRun || showOverride ? (
            <EmptyContent>
              <div className='flex flex-wrap justify-center gap-3'>
                {showOverride ? (
                  <Button type='button' variant='outline' onClick={onManualOverrideClick}>
                    {t('itemDetail.manualOverride.button')}
                  </Button>
                ) : null}
                {showRun ? (
                  <Button type='button' onClick={onRunAuditClick}>
                    {t('itemDetail.audits.runAudit')}
                  </Button>
                ) : null}
              </div>
            </EmptyContent>
          ) : null}
        </Empty>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {showHint ? (
        <Alert variant='info'>
          <AlertDescription>
            {t('itemDetail.audits.runAuditHint')}
          </AlertDescription>
        </Alert>
      ) : null}
      <AuditsTabActions
        showRunAudit={showRunAudit}
        onRunAuditClick={onRunAuditClick}
        showManualOverride={showManualOverride}
        onManualOverrideClick={onManualOverrideClick}
      />
      <PackagingFieldCrossCheckHistoryTable
        history={history}
        locale={locale}
      />
    </div>
  )
}
